import {
  combineReducers,
  configureStore,
  createAsyncThunk,
  createListenerMiddleware,
} from "@reduxjs/toolkit";
import localforage from "localforage";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createMigrate,
  persistReducer,
  persistStore,
} from "redux-persist";
import accountingApi from "../services/accounting";
import authApi from "../services/auth";
import salesApi from "../services/sales";
import airlines, { loadAirlines } from "./airlines";
import auth, {
  loginUser,
  refreshAccessToken,
  registerApis,
  selectCanEditChartOfAccounts,
  selectLoggedInUser,
} from "./auth";
import coa, { loadChartOfAccounts } from "./coa";
import customers, { loadCustomers, save as saveCustomer } from "./customers";
import gds, { loadGdsOffices } from "./gds";
import migrations from "./migrations";
import notifications from "./notifications";
import sales from "./sales";
import salesAgents, { loadSalesAgents } from "./salesAgents";
import search, { loadDefaultSearchResults, loadSearchResults } from "./search";
import { resetSuggestions } from "../suggestions";
import suppliers, { loadSuppliers } from "./suppliers";

const loadAllEntities = createAsyncThunk(
  "entities/loadAll",
  (_, { dispatch, getState }) => {
    resetSuggestions();
    dispatch(loadAirlines());
    dispatch(loadCustomers());
    dispatch(loadGdsOffices());
    dispatch(loadSalesAgents());
    dispatch(loadSuppliers());

    const canViewAccounting = selectCanEditChartOfAccounts(getState());
    if (canViewAccounting) {
      dispatch(loadChartOfAccounts());
    }
  },
);

const bootstrap = (store) => (err) => {
  if (err) {
    throw err;
  }
  const loggedInUser = selectLoggedInUser(store.getState());
  if (loggedInUser) {
    store
      .dispatch(refreshAccessToken())
      .unwrap()
      .then(() => {
        store.dispatch(loadAllEntities());
        store.dispatch(loadSearchResults());
      })
      .catch((err) => {});
  }
};

export const setupStore = (preloadedState, persist = true) => {
  const listenerMiddleware = createListenerMiddleware();

  listenerMiddleware.startListening({
    actionCreator: loginUser.fulfilled,
    effect: async (_, { dispatch, getState }) => {
      dispatch(loadAllEntities());
      dispatch(loadDefaultSearchResults());
    },
  });

  listenerMiddleware.startListening({
    actionCreator: saveCustomer.fulfilled,
    effect: async (_, { dispatch, getState }) => {
      dispatch(loadGdsOffices());
    },
  });

  const migrate = createMigrate(migrations, { debug: true });

  const reducer = persistReducer(
    {
      blacklist: ["sales", "notifications"],
      key: "azledger/",
      migrate,
      storage: localforage,
      version: 11,
    },
    combineReducers({
      airlines,
      auth,
      coa,
      customers,
      gds,
      notifications,
      sales,
      salesAgents,
      search,
      suppliers,
    }),
  );

  const store = configureStore({
    devTools: process.env.NODE_ENV !== "production",
    preloadedState,
    reducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE],
        },
      }).prepend(listenerMiddleware.middleware),
  });

  registerApis(store)([authApi, accountingApi, salesApi]);

  const persistor = persist
    ? persistStore(store, null, bootstrap(store))
    : null;

  return { persistor, store };
};
